import type React from "react"
import { Fragment } from "react"

import { Dialog, Transition } from "@headlessui/react"
import { useTranslations } from "next-intl"

import { ArrowBack } from "@/components/flow/icons/ArrowBack"
import { X } from "@/components/flow/icons/X"
import { ModalProvider, useModal } from "@/lib/providers/ModelProvider"

interface SheetProps {
  isOpen: boolean
  close: () => void
  children: React.ReactNode
  fullscreen?: boolean
}

export const SideSheet = ({ isOpen, close, children, fullscreen = false }: SheetProps) => {
  const t = useTranslations("common")
  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="z-modal relative" onClose={close}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/50" />
        </Transition.Child>

        <div className="fixed inset-0">
          <div className="absolute inset-0">
            <div className="pointer-events-none fixed inset-y-0 right-0 flex max-w-full">
              <Transition.Child
                as={Fragment}
                enter="transform transition ease-in-out duration-300"
                enterFrom="translate-x-full"
                enterTo="translate-x-0"
                leave="transform transition ease-in-out duration-300"
                leaveFrom="translate-x-0"
                leaveTo="translate-x-full"
              >
                <Dialog.Panel className={fullscreen ? "pointer-events-auto w-screen" : "pointer-events-auto w-screen max-w-md sm:max-w-lg md:max-w-xl lg:max-w-2xl"}>
                  <div className={fullscreen ? "bg-surface-warm relative flex h-full flex-col overflow-y-auto" : "bg-surface relative flex h-full flex-col shadow-xl"}>
                    {!fullscreen && (
                      <button
                        type="button"
                        aria-label={t("close")}
                        onClick={close}
                        className="bg-surface hover:bg-surface-muted absolute top-4 left-4 z-10 rounded-full p-2 transition-colors duration-200"
                      >
                        <ArrowBack size={30} />
                      </button>
                    )}
                    <ModalProvider close={close}>{children}</ModalProvider>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </div>
      </Dialog>
    </Transition>
  )
}

const Title: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { close } = useModal()
  const t = useTranslations("common")

  return (
    <Dialog.Title className="flex items-center justify-between p-4 sm:p-6">
      <div className="text-large-semi">{children}</div>
      <button type="button" aria-label={t("close")} onClick={close} className="text-gray-400 hover:text-gray-500">
        <X size={20} />
      </button>
    </Dialog.Title>
  )
}

const Description: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return <Dialog.Description className="text-small-regular text-ui-fg-base p-4 sm:p-6">{children}</Dialog.Description>
}

const Body: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return <div className="flex-1 overflow-hidden p-4 sm:p-6">{children}</div>
}

const Footer: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return <div className="flex items-center justify-end gap-x-4 p-4 sm:p-6">{children}</div>
}

SideSheet.Title = Title
SideSheet.Description = Description
SideSheet.Body = Body
SideSheet.Footer = Footer
